export const SERVICIO_KEYWORDS = [
  'aconcagua',
  'paico',
  'ochagavia',
  'bresler',
  'carozzi',
  'icb',
  'quantum',
  'melon',
  'melón',
  'codelco',
  'ventana',
  'ventanas',
  'polpaico',
  'andina',
  'watts',
  'ccu',
  'anglo',
  'soprole',
  // Clientes "en levantamiento"/"prelevantamiento" (carteras Levantamiento y
  // Prelevantamiento, sin servicio/cobertura aún). Se registran con un punto final
  // en el nombre (ej. "IDEAL.") para que el keyword sea un match exacto y no choque
  // con clientes ya existentes que contienen la misma palabra suelta (ej.
  // "IDEAL - QUILICURA", "VIÑA SAN PEDRO - MOLINA").
  // Alias/variantes del levantamiento de Molina/Molino para que no se pierda
  // la clasificación al cambiar el nombre visible o la carga de datos.
  'molino.',
  'molina.',
  'castaño.',
  'ideal.',
  'volcan.',
  // SOPRAVAL — con A, no "Soproval": así está registrado el cliente
  // ("SOPRAVAL - LA CALERA", id 9, cartera Levantamiento). Ojo con la confusión, porque
  // 'soprole' de arriba es otra empresa distinta.
  //
  // Va SIN punto final, a diferencia de los de arriba: el punto sirve cuando el keyword
  // es una palabra genérica que también aparece en clientes ya existentes ("ideal" está
  // dentro de "IDEAL - QUILICURA") y ahí obliga a un match exacto. "sopraval" es un
  // nombre propio que no choca con nada, y sin punto calza con el guión del nombre real.
  //
  // Que el keyword cubra a toda la empresa y no solo a La Calera NO abre la puerta a las
  // otras plantas: en Planificación cada cliente pasa además por visible_en_chart (ver
  // isClienteVisible en utils/clientFilters.ts), que es el interruptor por sede. De
  // hecho hoy "SOPRAVAL - QUILPUE" existe con ese flag en false y por eso no aparece.
  'sopraval',
  // UNILEVER - CONCHALI (id 777, cartera Instalación). Igual que sopraval, el keyword
  // cubre a la empresa y el interruptor por sede es visible_en_chart: "UNILEVER" (id 776,
  // Industria 2) no tiene el flag y sigue sin aparecer.
  'unilever',
];

export const normalizeForCompare = (s: string | undefined) => {
  if (!s) return '';
  try {
    return s.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return (s || '').toString().toLowerCase();
  }
};

export const tieneServicio = (nombre: string | undefined): boolean => {
  const n = normalizeForCompare(nombre);
  return SERVICIO_KEYWORDS.some(k => n.includes(normalizeForCompare(k)));
};

// ─── Lista explícita de clientes servicio ────────────────────────────────────

export const ALLOWED_CLIENTS = [
  'carozzi pasta', 'carozzi chocolate', 'carozzi caramelos', 'carozzi bresler',
  'carozzi galletas', 'icb', 'codelco ventana', 'ariztia el paico',
  'ariztia ochagavia', 'cemento melon - la calera', 'cemento melon - ventanas',
  'codelco ventanas', 'quantum', 'aconcagua foods', 'anglo',
];

export const FORBIDDEN_CLIENTS_SUBSTR = [
  'carozzi - planta nos',
  'carozzi nos',
  'carozzi - planta lontue',
  'carozzi - planta teno',
  'cemento polpaico - sa',
  'empresa electrica ventana',
  'icb cao - import do brasil',
];

const tokenize = (s: string): string[] => {
  const m = (s || '').toString().toLowerCase().match(/\w+/g);
  return m ? m : [];
};

export const isAllowedClient = (name: string): boolean => {
  const nameTokens = tokenize(name);
  return ALLOWED_CLIENTS.some(allowed => tokenize(allowed).every(t => nameTokens.includes(t)));
};

export const isForbiddenClient = (name: string): boolean => {
  const n = (name || '').toString().toLowerCase().trim();
  return FORBIDDEN_CLIENTS_SUBSTR.some(sub => n.includes(sub));
};

export const esClienteServicio = (name: string): boolean =>
  isAllowedClient(name) && !isForbiddenClient(name);

// ─── Override manual de servicios (persiste en localStorage) ─────────────────

export interface ServicioOverrides {
  include: number[]; // IDs de clientes marcados manualmente como servicio
  exclude: number[]; // IDs de clientes excluidos manualmente del filtro de servicio
}

const OVERRIDE_KEY = 'servicio_overrides_v1';

export const readServicioOverrides = (): ServicioOverrides => {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDE_KEY) || 'null') || { include: [], exclude: [] };
  } catch {
    return { include: [], exclude: [] };
  }
};

const writeServicioOverrides = (o: ServicioOverrides) => {
  try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify(o)); } catch {}
};

/**
 * Alterna el estado de servicio de un cliente.
 * Si era servicio (por keyword o include), lo excluye.
 * Si no era servicio, lo incluye.
 */
export const toggleServicioOverride = (clienteId: number, currentIsServicio: boolean): ServicioOverrides => {
  const o = readServicioOverrides();
  const inc = new Set(o.include);
  const exc = new Set(o.exclude);
  if (currentIsServicio) {
    inc.delete(clienteId);
    exc.add(clienteId);
  } else {
    exc.delete(clienteId);
    inc.add(clienteId);
  }
  const next = { include: Array.from(inc), exclude: Array.from(exc) };
  writeServicioOverrides(next);
  return next;
};

/**
 * Determina si un cliente es "servicio" considerando overrides manuales.
 * Prioridad: exclude > include > keyword match.
 */
export const esServicio = (
  nombre: string | undefined,
  clienteId: number,
  overrides: ServicioOverrides,
): boolean => {
  if (overrides.exclude.includes(clienteId)) return false;
  if (overrides.include.includes(clienteId)) return true;
  return tieneServicio(nombre);
};
